import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthProvider } from './hooks/useAuth';
import { UserStateProvider, useUserState } from './hooks/useUserState';
import Header from './components/layout/Header';
import { OfflineBanner } from './components/OfflineBanner';
import HomePage from './components/game/HomePage';
import GameScreen from './components/game/GameScreen';
import ResultsScreen from './components/game/ResultsScreen';
import { ProfileScreen } from './components/ProfileScreen';
import { LeaderboardScreen } from './components/LeaderboardScreen';
import { AchievementNotification } from './components/AchievementNotification';
import { AuthCallback } from './components/auth/AuthCallback';
import { GameState, GameMode } from './types';
import { UserProfile, GameSession, UnlockedAchievement } from './types/profile';
import { generateQuestionForGameMode } from './utils/gameLogic';
import { createInitialPowerUps } from './data/powerUps';
import { gameModeConfigs } from './data/gameObjects';
import audioFileService from './services/audioFileService';
import { UnifiedProfileService } from './services/unifiedProfileService';

const initialGameState: GameState = {
  score: 0,
  streak: 0,
  questionsAnswered: 1,
  correctAnswers: 0,
  totalQuestions: 10,
  difficulty: 'easy',
  gameMode: 'classic',
  isGameActive: false,
  currentQuestion: null,
  currentMeasurementType: undefined,
};

function AppContent() {
  const { userState, loading: userStateLoading } = useUserState();
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [gameStartTime, setGameStartTime] = useState<Date | null>(null);
  const [newAchievements, setNewAchievements] = useState<UnlockedAchievement[]>([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize profile based on user state
  const initializeProfile = async () => {
    if (userStateLoading) return;
    
    setProfileLoading(true);
    
    try {
      const loadedProfile = await UnifiedProfileService.getProfile(
        userState.type,
        userState.user?.id
      );
      setProfile(loadedProfile);
    } catch (error) {
      console.error('Error initializing profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Initialize profile when user state changes
  useEffect(() => {
    initializeProfile();
  }, [userState.type, userState.user?.id, userStateLoading]);

  // Auto-sync when coming online (authenticated users only)
  useEffect(() => {
    if (userState.type === 'authenticated-online' && userState.user && profile) {
      const syncData = async () => {
        try {
          const syncResult = await UnifiedProfileService.syncWhenOnline(userState.user!.id);
          if (syncResult.success) {
            console.log('Auto-sync completed successfully');
            // Refresh profile after sync
            await initializeProfile();
          } else {
            console.warn('Auto-sync failed:', syncResult.error);
          }
        } catch (error) {
          console.error('Auto-sync failed:', error);
        }
      };
      
      syncData();
    }
  }, [userState.type]);

  const startGame = async (difficulty: 'easy' | 'medium' | 'hard' | 'mixed', gameMode: GameMode, measurementType?: 'length' | 'weight' | 'volume', categories?: string[]) => {
    // Prepare audio context and play game start sound
    await audioFileService.prepareAudio();
    audioFileService.play('gameStart');

    // Setup power-ups for power-ups mode
    const powerUps = gameMode === 'power_ups' ? createInitialPowerUps() : undefined;
    const lives = gameMode === 'power_ups' ? 5 : undefined;

    // Get game mode configuration
    const gameModeConfig = gameModeConfigs.find(config => config.id === gameMode);
    const timeLimit = gameModeConfig?.settings.timeLimit;
    const totalQuestions = timeLimit ? Infinity : (gameModeConfig?.settings.totalQuestions || 10);

    // Generate first question
    const firstQuestion = generateQuestionForGameMode(gameMode, difficulty, measurementType, categories);

    setGameState({
      ...initialGameState,
      difficulty,
      gameMode,
      currentMeasurementType: measurementType,
      selectedCategories: categories,
      isGameActive: true,
      currentQuestion: firstQuestion,
      totalQuestions,
      powerUps,
      lives,
      activePowerUps: {},
      timeLimit,
      timeRemaining: timeLimit
    });

    setGameStartTime(new Date());
    navigate('/game');
  };

  const nextQuestion = () => {
    // For timed mode, don't end based on question count
    if (gameState.gameMode !== 'timed' && gameState.questionsAnswered >= gameState.totalQuestions) {
      endGameSession();
      navigate('/results');
      return;
    }

    // Generate next question
    const nextQ = generateQuestionForGameMode(
      gameState.gameMode, 
      gameState.difficulty,
      gameState.currentMeasurementType,
      gameState.selectedCategories
    );

    setGameState(prev => ({
      ...prev,
      questionsAnswered: prev.questionsAnswered + 1,
      currentQuestion: nextQ
    }));
  };

  const updateScore = (points: number, isCorrect: boolean) => {
    setGameState(prev => {
      const newStreak = isCorrect ? prev.streak + 1 : 0;
      const newCorrectAnswers = isCorrect ? prev.correctAnswers + 1 : prev.correctAnswers;
      
      return {
        ...prev,
        score: prev.score + points,
        streak: newStreak,
        correctAnswers: newCorrectAnswers
      };
    });
  };

  const endGameSession = async () => {
    if (!gameStartTime) return;

    const endTime = new Date();
    const timePlayed = Math.floor((endTime.getTime() - gameStartTime.getTime()) / 1000);

    // Create game session data
    const gameSession: GameSession = {
      gameMode: gameState.gameMode,
      difficulty: gameState.difficulty,
      measurementType: gameState.currentMeasurementType,
      score: gameState.score,
      questionsAnswered: gameState.questionsAnswered,
      correctAnswers: gameState.correctAnswers,
      streak: gameState.streak,
      timePlayed,
      startedAt: gameStartTime.toISOString(),
      completedAt: endTime.toISOString()
    };

    try {
      // Save game session using unified service
      const unlockedAchievements = await UnifiedProfileService.saveGameSession(
        gameSession,
        userState.type,
        userState.user?.id
      );
      
      // Refresh profile
      await initializeProfile();
      
      if (unlockedAchievements.length > 0) {
        audioFileService.play('achievement');
        setNewAchievements(unlockedAchievements);
      }

      // Play game end sound
      audioFileService.play('gameEnd');

      // End the game
      setGameState(prev => ({
        ...prev,
        isGameActive: false,
        currentQuestion: null,
      }));

    } catch (error) {
      console.error('Error saving game session:', error);
      // Still end the game even if saving fails
    setGameState(prev => ({
      ...prev,
        isGameActive: false,
        currentQuestion: null,
    }));
    }
  };

  const resetGame = () => {
    setGameState(initialGameState);
    setGameStartTime(null);
    navigate('/');
  };

  const endTimedGame = async () => {
    await endGameSession();
    navigate('/results');
  };

  const updateGameState = (updates: Partial<GameState>) => {
    setGameState(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleAchievementsDismiss = () => {
    setNewAchievements([]);
  };

  const navigateToProfile = () => {
    navigate('/profile');
  };

  const navigateToLeaderboard = () => {
    navigate('/leaderboard');
  };

  const navigateHome = () => {
    navigate('/');
  };

  // Show loading while user state or profile is loading
  if (userStateLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-banana-100 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🍌</div>
          <div className="text-xl font-semibold text-banana-800">Loading your profile...</div>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-banana-100 to-orange-100">
      <OfflineBanner />
      <Header 
        profile={profile}
        onProfileClick={navigateToProfile}
      />
        <main className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Routes>
              <Route 
                path="/" 
                element={
                  <HomePage 
                    onStartGame={startGame}
                    gameState={gameState}
                  profile={profile}
                  onLeaderboardClick={navigateToLeaderboard}
                  />
                } 
              />
              <Route 
                path="/game" 
                element={
                  <GameScreen 
                    gameState={gameState}
                    onNextQuestion={nextQuestion}
                    onUpdateScore={updateScore}
                    onEndGame={resetGame}
                    onEndTimedGame={endTimedGame}
                    onUpdateGameState={updateGameState}
                  />
                } 
              />
              <Route 
                path="/results" 
                element={
                  <ResultsScreen 
                    gameState={gameState}
                  onPlayAgain={() => startGame(gameState.difficulty, gameState.gameMode, gameState.currentMeasurementType)}
                    onBackToHome={resetGame}
                  profile={profile}
                />
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProfileScreen 
                  onBack={navigateHome}
                />
              } 
            />
            <Route 
              path="/leaderboard" 
              element={
                <LeaderboardScreen 
                  onBack={navigateHome}
                  />
                } 
              />
              <Route 
                path="/auth/callback" 
                element={<AuthCallback />} 
              />
            </Routes>
          </motion.div>
        </main>

      {/* Achievement Notifications */}
      <AchievementNotification 
        achievements={newAchievements}
        onDismiss={handleAchievementsDismiss}
      />
      </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <UserStateProvider>
          <AppContent />
        </UserStateProvider>
      </AuthProvider>
    </Router>
  );
}

export default App; 